import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from '@bloo-team/ui'
import { Copy, History, Search } from '@bloo-team/ui/icons'
import { priorOnEntity } from '../../lib/signals'
import { relatedSignals } from '../../lib/signal-hunt'
import { monthDayTime, timeSpan } from '../../lib/local-time'
import {
  DISPOSITION_LABEL,
  KILL_CHAIN,
  entityKey,
  reasonSentence,
  stagesFor,
  type Disposition,
  type Signal,
  type SignalEntity,
} from '../../lib/signal-model'
import { agoLabel, copyText, entitySearchQuery } from './panel-utils'
import type { SignalPanelProps } from './types'

/**
 * What the signal panel's two placements share (docs/signals.md §The signal
 * pane and panel): the derivations, and the sections both render — entities,
 * storyline members, cases, prior handling, notes/audit. The sheet stacks them
 * as a long scroll (SignalPanel); the docked pane spreads them over tabs
 * (SignalPane).
 */

type DerivedInput = Pick<SignalPanelProps, 'signal' | 'triage' | 'all' | 'storyline' | 'triageMap' | 'baseline'>

export function useSignalDerived({ signal, triage, all, storyline, triageMap, baseline }: DerivedInput) {
  const related = useMemo(() => relatedSignals(signal, all), [signal, all])
  /** The wider pool for counts and prior handling: the 30d baseline when the page hands it in. */
  const pool = baseline ?? all
  const relatedInPool = useMemo(() => (baseline ? relatedSignals(signal, baseline) : related), [signal, baseline, related])

  const reason = useMemo(
    () =>
      reasonSentence(signal, {
        priorOnEntity: priorOnEntity(signal, all, entityKey),
        storylineScore: storyline?.promoted ? storyline.score : null,
      }),
    [signal, all, storyline],
  )

  /** Every signal we hold by uuid — resolves a subgraph's kept and folded uuids to full rows when they are loaded. */
  const byUuid = useMemo(() => new Map([...(baseline ?? []), ...all].map((s) => [s.uuid, s])), [all, baseline])
  /** Same-entity fallback members, used only when no storyline was resolved. */
  const members = useMemo(() => {
    if (storyline) return null
    return [signal, ...related].sort((a, b) => b.at - a.at)
  }, [storyline, signal, related])
  /** Storyline members: the subgraph's kept signals (folded uuids listed under each). */
  const kept = useMemo(() => (storyline ? [...storyline.signals].sort((a, b) => (b.end ?? 0) - (a.end ?? 0)) : null), [storyline])
  const stages = useMemo(
    () => stagesFor(storyline ? storyline.tactics : (members ?? []).map((m) => m.tactic)),
    [storyline, members],
  )

  const prior = useMemo(() => {
    const byDisposition = new Map<Disposition, number>()
    let closedCount = 0
    let last: { who: string; at: number } | null = null
    for (const s of pool) {
      if (s.detectionName !== signal.detectionName) continue
      const t = triageMap.get(s.uuid)
      if (!t) continue
      if (t.status === 'closed' && t.disposition) {
        closedCount++
        byDisposition.set(t.disposition, (byDisposition.get(t.disposition) ?? 0) + 1)
      }
      const entry = t.log[t.log.length - 1]
      if (entry && (!last || entry.at > last.at)) last = { who: entry.who, at: entry.at }
    }
    return { closedCount, byDisposition, last }
  }, [pool, signal.detectionName, triageMap])

  const audit = useMemo(() => {
    const notes = (triage?.notes ?? []).map((n) => ({ at: n.at, who: n.who, text: n.text, kind: 'note' as const, id: n.id }))
    const log = (triage?.log ?? []).map((l, i) => ({ at: l.at, who: l.who, text: l.what, kind: 'log' as const, id: `log-${i}` }))
    return [...notes, ...log].sort((a, b) => a.at - b.at)
  }, [triage])

  const countFor = (e: SignalEntity) => {
    const k = entityKey(e)
    return relatedInPool.filter((s) => s.entities.some((x) => entityKey(x) === k)).length
  }
  const poolLabel = baseline ? '30d' : 'window'
  const memberCount = storyline ? storyline.uuids.length : (members?.length ?? 0)

  return { reason, byUuid, members, kept, stages, prior, audit, countFor, poolLabel, memberCount }
}

export type SignalDerived = ReturnType<typeof useSignalDerived>

export function Section({ eyebrow, aside, children }: { eyebrow: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="sp-section">
      <div className="hud-row hud-row-between sp-section-head">
        <span className="hud-eyebrow">{eyebrow}</span>
        {aside}
      </div>
      {children}
    </section>
  )
}

/* -------------------------------------------------------------- entities */

export function EntityList({
  signal,
  derived,
  entityContext,
  onOpenEntity,
  onOpenSearch,
}: Pick<SignalPanelProps, 'signal' | 'entityContext' | 'onOpenEntity' | 'onOpenSearch'> & { derived: SignalDerived }) {
  if (signal.entities.length === 0) return <p className="q-facet-note">No Suspect/Target fields on this signal.</p>
  return (
    <ul className="sp-entities">
      {signal.entities.map((e) => {
        const n = derived.countFor(e)
        const ctx = entityContext?.[entityKey(e)]
        return (
          <li key={`${e.role}${e.field}:${e.value}`} className="sp-entity" data-role={e.role}>
            <span className="sp-entity-role">{e.role}</span>
            <span className="sp-entity-field">{e.field.toLowerCase()}</span>
            <span className="sp-entity-value">{e.value}</span>
            <span className="sp-entity-n" data-rare={ctx?.rare === true ? '' : undefined}>
              {n} other signal{n === 1 ? '' : 's'}
              {ctx
                ? ` · ${ctx.first === null ? 'first seen now' : `first ${agoLabel(ctx.first)}`}${ctx.rare === true ? ' · rare' : ''}`
                : ''}
            </span>
            <span className="sp-entity-pivots">
              <button
                type="button"
                className="qbc-x"
                aria-label={`Entity history for ${e.field} ${e.value}`}
                title="Entity history"
                onClick={() => onOpenEntity(e)}
              >
                <Icon icon={History} size={11} />
              </button>
              <button
                type="button"
                className="qbc-x"
                aria-label={`Search events for ${e.field} ${e.value}`}
                title="Search events"
                onClick={() => onOpenSearch(entitySearchQuery(e))}
              >
                <Icon icon={Search} size={11} />
              </button>
              <button
                type="button"
                className="qbc-x"
                aria-label={`Copy value ${e.value}`}
                title="Copy value"
                onClick={() => void copyText(e.value)}
              >
                <Icon icon={Copy} size={11} />
              </button>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------- storyline members */

export function StorylineMembers({
  signal,
  derived,
  onSelectSignal,
}: Pick<SignalPanelProps, 'signal' | 'onSelectSignal'> & { derived: SignalDerived }) {
  const { kept, members, byUuid, stages } = derived
  return (
    <>
      <div className="sp-chain" aria-label="kill chain">
        {KILL_CHAIN.map((stage) => (
          <span key={stage} className="sp-chain-box" data-hit={stages.has(stage) ? '' : undefined}>
            {stage}
          </span>
        ))}
      </div>
      <ol className="sp-timeline">
        {kept
          ? kept.map((k) => {
              const me = k.uuid === signal.uuid || k.folded.includes(signal.uuid)
              const row = byUuid.get(k.uuid)
              return (
                <li key={k.uuid}>
                  <button
                    type="button"
                    className="sp-tl-row"
                    aria-current={me ? 'true' : undefined}
                    onClick={() => {
                      if (k.uuid !== signal.uuid) onSelectSignal(k.uuid)
                    }}
                  >
                    <span className="sp-tl-time">{monthDayTime(k.end)}</span>
                    <span className="sp-sev" data-severity={k.severity} />
                    <span className="sp-tl-name">{k.detectionName}</span>
                    <span className="sp-tl-ents">{row ? row.entities.map((e) => e.value).join(' · ') : ''}</span>
                  </button>
                  {k.count > 1 ? (
                    <details className="sg-fold">
                      <summary>{`×${k.count} · ${timeSpan(k.start, k.end)}`}</summary>
                      <ul>
                        {k.folded.map((u) => {
                          const f = byUuid.get(u)
                          return (
                            <li key={u}>
                              <button
                                type="button"
                                className="sp-tl-row"
                                aria-current={u === signal.uuid ? 'true' : undefined}
                                onClick={() => {
                                  if (u !== signal.uuid) onSelectSignal(u)
                                }}
                              >
                                <span className="sp-tl-time">{f ? monthDayTime(f.at) : '—'}</span>
                                <span className="sp-tl-name mono">{u}</span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </details>
                  ) : null}
                </li>
              )
            })
          : (members ?? []).map((m: Signal) => {
              const me = m.uuid === signal.uuid
              return (
                <li key={m.uuid}>
                  <button
                    type="button"
                    className="sp-tl-row"
                    aria-current={me ? 'true' : undefined}
                    onClick={() => {
                      if (!me) onSelectSignal(m.uuid)
                    }}
                  >
                    <span className="sp-tl-time">{monthDayTime(m.at)}</span>
                    <span className="sp-sev" data-severity={m.severity} />
                    <span className="sp-tl-name">{m.detectionName}</span>
                    <span className="sp-tl-ents">{m.entities.map((e) => e.value).join(' · ')}</span>
                  </button>
                </li>
              )
            })}
      </ol>
    </>
  )
}

/* ------------------------------------------------------------------ cases */

/* --------------------------------------------------------- prior handling */

export function PriorHandling({
  derived,
  suppressions,
  baseline,
}: Pick<SignalPanelProps, 'suppressions' | 'baseline'> & { derived: SignalDerived }) {
  const { prior } = derived
  if (prior.closedCount === 0 && !prior.last && !suppressions?.length) {
    return <p className="q-facet-note">No triage history for this detection in the {baseline ? 'last 30 days' : 'window'}.</p>
  }
  return (
    <dl className="kv">
      <dt>closed</dt>
      <dd>
        {prior.closedCount === 0
          ? 'none'
          : [...prior.byDisposition.entries()].map(([d, n]) => `${n} ${DISPOSITION_LABEL[d].toLowerCase()}`).join(', ')}
      </dd>
      <dt>last handler</dt>
      <dd>{prior.last ? `${prior.last.who} · ${agoLabel(prior.last.at)}` : '—'}</dd>
      <dt>suppression</dt>
      <dd>
        {suppressions && suppressions.length > 0 ? (
          <>
            {suppressions.length} suppression rule{suppressions.length === 1 ? '' : 's'} match these entities
            <ul className="sp-rules">
              {suppressions.map((r) => (
                <li key={r.id} className="mono">
                  {r.summary}
                </li>
              ))}
            </ul>
          </>
        ) : (
          'no matching rules'
        )}
      </dd>
    </dl>
  )
}

/* ------------------------------------------------------------ notes/audit */

export function NotesAudit({ derived, onAddNote }: Pick<SignalPanelProps, 'onAddNote'> & { derived: SignalDerived }) {
  const [note, setNote] = useState('')
  const submitNote = () => {
    const text = note.trim()
    if (!text) return
    onAddNote(text)
    setNote('')
  }
  return (
    <>
      {derived.audit.length ? (
        <ol className="sp-audit">
          {derived.audit.map((a) => (
            <li key={a.id} className="sp-audit-row" data-kind={a.kind}>
              <span className="sp-audit-meta">
                {a.who} · {agoLabel(a.at)}
              </span>
              <span className="sp-audit-text">{a.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="q-facet-note">No notes yet.</p>
      )}
      <div className="hud-stack sp-note-form">
        <textarea
          className="bui-input"
          aria-label="New note"
          placeholder="add a note…"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div>
          <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" disabled={!note.trim()} onClick={submitNote}>
            Add note
          </button>
        </div>
      </div>
    </>
  )
}
